/**
 * ч.1357: Кабінет респондента — журнал подань статзвітів до Держстату: статуси,
 * відправка підписаних, отримання квитанцій ключем, тексти квитанцій. Аналог Кабінету
 * ДПС, але для приймального шлюзу Держстату (Порядок № 3: подання напряму, погодження
 * ПЗ не потрібне).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  QUARTER_LABELS, SLUG_BY_FORM, FORM_SHORT, SUB_STATUS_COLOR, SUB_STATUS_LABEL,
  statReports, statSubmissions, type StatReport, type StatSubmission,
} from '@/api/statReports';
import { orgs as orgsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useConfirm } from '@/components/ConfirmProvider';
import { fetchStatReceipts, receiptEmails, StatKeyBox, useStatKey, type ReceiptsOutcome } from './StatSignPanel';

export default function StatCabinetPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const k = useStatKey();
  const { availableOrgs } = usePermissionsByOrg('reports.stat.manage');
  const [orgId, setOrgId] = useOrgFilter();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ReceiptsOutcome | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list({ limit: 200 }) });
  const subsQ = useQuery({ queryKey: ['stat-subs', orgId], queryFn: () => statSubmissions.list({ organization_id: orgId || undefined, limit: 500 }) });
  const reportsQ = useQuery({ queryKey: ['stat-reports-all', orgId], queryFn: () => statReports.list({ organization_id: orgId || undefined, limit: 500 }) });
  const msgsQ = useQuery({ queryKey: ['stat-msgs', orgId], queryFn: () => statSubmissions.messages({ organization_id: orgId || undefined, unprocessed: true }) });
  const reportById = useMemo(() => {
    const m = new Map<string, StatReport>();
    for (const r of reportsQ.data?.items ?? []) m.set(r.id, r);
    return m;
  }, [reportsQ.data]);
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgListQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgListQ.data]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['stat-subs'] }); qc.invalidateQueries({ queryKey: ['stat-msgs'] }); qc.invalidateQueries({ queryKey: ['stat-subs-all'] }); };
  const onErr = (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e));

  async function sendSub(s: StatSubmission) {
    setBusyId(s.id); setError(null);
    try { const r = await statSubmissions.send(s.id); if (r.gate_result !== 'GATE_OK') setError(`Шлюз Держстату: ${r.message}`); refresh(); }
    catch (e: any) { onErr(e); } finally { setBusyId(null); }
  }
  async function receipts() {
    const org = orgId || availableOrgs[0]?.id;
    if (!org) { setError('Оберіть організацію'); return; }
    const card = orgListQ.data?.items.find((o) => o.id === org)?.email ?? '';
    const emails = receiptEmails(card, (subsQ.data?.items ?? []).filter((s) => s.organization_id === org));
    if (!emails.length) { setError('У картці організації не вказано e-mail — на нього шлюз надсилає квитанції'); return; }
    setBusyId('receipts'); setError(null);
    try {
      const o: ReceiptsOutcome = { received: 0, linked: [], errors: [] };
      for (const email of emails) {
        const one = await fetchStatReceipts(org, email);
        o.received += one.received; o.linked.push(...one.linked); o.errors.push(...one.errors);
      }
      setOutcome(o); if (o.errors.length) setError(o.errors.join('\n')); refresh();
    }
    catch (e: any) { onErr(e); } finally { setBusyId(null); }
  }
  async function removeSub(s: StatSubmission) {
    if (!(await confirm('Прибрати подання з журналу? Сам звіт лишається.'))) return;
    setBusyId(s.id); setError(null);
    try { await statSubmissions.remove(s.id); refresh(); } catch (e: any) { onErr(e); } finally { setBusyId(null); }
  }

  const cols = useMemo<ColumnDef<StatSubmission>[]>(() => [
    { key: 'form', header: 'Форма', width: 150, cell: (s) => FORM_SHORT[s.form_code] ?? s.form_code, sortValue: (s) => s.form_code },
    { key: 'period', header: 'Період', width: 140,
      cell: (s) => { const r = reportById.get(s.report_id); return r ? `${QUARTER_LABELS[r.period_quarter]} ${r.period_year}` : '—'; },
      sortValue: (s) => { const r = reportById.get(s.report_id); return r ? r.period_year * 10 + r.period_quarter : 0; } },
    { key: 'org', header: 'Організація', cell: (s) => orgName[s.organization_id] ?? '', sortValue: (s) => orgName[s.organization_id] ?? '' },
    { key: 'file', header: 'Файл', width: 300, cell: (s) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{s.file_name || '—'}</span> },
    { key: 'status', header: 'Стан', width: 190,
      cell: (s) => <span style={{ color: SUB_STATUS_COLOR[s.status], fontWeight: s.status === 'ACCEPTED' ? 600 : 400 }}>{SUB_STATUS_LABEL[s.status]}</span>,
      sortValue: (s) => s.status },
    { key: 'sent', header: 'Відправлено', width: 150, cell: (s) => s.sent_at ? new Date(s.sent_at).toLocaleString('uk-UA') : '', sortValue: (s) => s.sent_at ?? '' },
    { key: 'actions', header: '', width: 260, cell: (s) => (
      <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <button onClick={() => navigate(`/stat-reports/${SLUG_BY_FORM[s.form_code]}/${s.report_id}`)}>звіт</button>
        {(s.status === 'SIGNED' || s.status === 'ERROR') && (
          <button onClick={() => sendSub(s)} disabled={busyId === s.id} title="Відправити збережений підписаний контейнер (ключ не потрібен)">відправити</button>
        )}
        {(s.receipt_text || s.kvt1_xml || s.kvt2_xml) && <button onClick={() => setOpen(open === s.id ? null : s.id)}>{open === s.id ? 'сховати' : 'квитанції'}</button>}
        {['DRAFT', 'SIGNED', 'ERROR'].includes(s.status) && <button className="danger" onClick={() => removeSub(s)} disabled={busyId === s.id}>×</button>}
      </span>
    ) },
  ], [reportById, orgName, busyId, open]);

  const rows = subsQ.data?.items ?? [];
  const opened = rows.find((s) => s.id === open) ?? null;
  const pending = msgsQ.data?.total ?? 0;

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Кабінет респондента</h2>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>подання статистичної звітності у приймальний шлюз Держстату, квитанції</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/stat-reports/1-opt/new')}>+ 1-опт</button>
          <button onClick={() => navigate('/stat-reports/10-zez/new')}>+ 10-зез</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 240 }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <div style={{ flex: 1, minWidth: 320 }}>
          <StatKeyBox k={k} />
          {k.owner && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              <button className="primary" onClick={receipts} disabled={busyId === 'receipts'} title="Запитати у шлюзу квитанції організації (підписаний e-mail), розшифрувати ключем">
                {busyId === 'receipts' ? 'запит…' : 'отримати квитанції'}
              </button>
              <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
                {orgId ? '' : 'для організації за замовчуванням — '}
                {pending ? `${pending} нерозшифрованих повідомлень` : 'квитанції приходять через кілька хвилин після відправки'}
              </span>
            </div>
          )}
        </div>
      </div>
      {outcome && (
        <div style={{ background: 'var(--bg-soft)', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          Отримано повідомлень: {outcome.received}
          {outcome.linked.map((l, i) => (
            <div key={i}>• {l.kind === 'KVT1' ? 'квитанція №1' : l.kind === 'KVT2' ? 'квитанція №2' : l.kind === 'NOTICE' ? 'повідомлення про повторне подання' : 'повідомлення'}
              {l.file_name ? ` (${l.file_name})` : ''}: {l.text?.slice(0, 160) || (l.accepted === false ? 'не прийнято' : 'одержано')}</div>
          ))}
        </div>
      )}
      {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}
      <DataTable<StatSubmission> rows={rows} columns={cols} rowKey={(s) => s.id} loading={subsQ.isLoading}
        emptyText="Подань ще немає — створіть звіт 1-опт або 10-зез і підпишіть його ключем" storageKey="stat-cabinet" initialSortKey="sent" initialSortDir="desc" />
      {opened && (
        <div style={{ marginTop: '0.75rem', border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.75rem', fontSize: '0.85rem' }}>
          <b>Квитанції Держстату — {opened.file_name}</b>
          {opened.receipt_text && <div style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{opened.receipt_text}</div>}
          {[['Квитанція №1', opened.kvt1_xml], ['Квитанція №2', opened.kvt2_xml], ['Повідомлення', opened.notice_xml]].map(([t, x]) => x ? (
            <details key={t} style={{ marginTop: 6 }}>
              <summary>{t} — XML</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.72rem', background: 'var(--bg-soft)', padding: '0.5rem', borderRadius: 6 }}>{x}</pre>
            </details>
          ) : null)}
        </div>
      )}
    </div>
  );
}
